/**
 * Inner Circle Management Section
 * Manages inner circle lists with source tracking and Beehiiv integration
 * 
 * Last Updated: March 18, 2026
 * Owner: Naomi (Super Admin)
 */
import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Users, Plus, RefreshCw, Eye, Edit3, Trash2, Search,
  Mail, CheckCircle2, Clock, TrendingUp, UserPlus,
  Tag, ExternalLink, BarChart3, ArrowUpRight, 
  Circle, Sparkles, Building, Calendar
} from "lucide-react";

// Source options matching backend
const SOURCE_OPTIONS = [
  { value: "hthf", label: "HTHF Event", color: "bg-blue-500" },
  { value: "front_row", label: "Front Row", color: "bg-purple-500" },
  { value: "partner_org", label: "Partner Org", color: "bg-pink-500" },
  { value: "platform", label: "Platform", color: "bg-cyan-500" },
  { value: "direct", label: "Direct Signup", color: "bg-green-500" },
  { value: "referral", label: "Referral", color: "bg-orange-500" },
  { value: "waitlist", label: "Waitlist", color: "bg-yellow-500" },
  { value: "other", label: "Other", color: "bg-gray-500" },
];

const getSourceColor = (source) => {
  const found = SOURCE_OPTIONS.find(s => s.value === source);
  return found ? found.color : "bg-gray-500";
};

const getSourceLabel = (source) => {
  const found = SOURCE_OPTIONS.find(s => s.value === source);
  return found ? found.label : source;
};

export default function InnerCircleSection() {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [members, setMembers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [authError, setAuthError] = useState(false);
  
  // Modal states
  const [showCreateList, setShowCreateList] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showMemberDetail, setShowMemberDetail] = useState(null);
  
  // Form states
  const [newList, setNewList] = useState({
    name: "",
    owner_type: "org",
    owner_id: "",
    description: "",
    visibility: { cohosts_can_see: true, show_source_sublists: true }
  });
  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    company: "",
    title: "",
    source: "direct",
    source_detail: "",
    interests: [],
    notes: ""
  });

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("session_token")}`
  });

  // Fetch all lists
  const fetchLists = useCallback(async () => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      console.log("No session token found, cannot fetch inner circle lists");
      setAuthError(true);
      setLoading(false);
      return;
    }
    setAuthError(false);
    try {
      const res = await fetch(`${API}/inner-circle/lists`, { headers: getHeaders() });
      if (res.status === 403) {
        setAuthError(true);
        toast.error("Admin access required");
      } else if (res.ok) {
        const data = await res.json();
        setLists(data.lists || []);
        if (data.lists?.length > 0 && !selectedList) {
          setSelectedList(data.lists[0]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch inner circle lists:", err);
      toast.error("Failed to load inner circle lists");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch members for selected list
  const fetchMembers = useCallback(async () => {
    if (!selectedList) return;
    setMembersLoading(true);
    try {
      let url = `${API}/inner-circle/lists/${selectedList.list_id}/members?limit=100`;
      if (sourceFilter && sourceFilter !== "all") {
        url += `&source=${sourceFilter}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (err) {
      console.error("Failed to fetch members:", err);
    } finally {
      setMembersLoading(false);
    }
  }, [selectedList, sourceFilter, searchQuery]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      let url = `${API}/inner-circle/analytics/sources`;
      if (selectedList) {
        url += `?list_id=${selectedList.list_id}`;
      }
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    }
  }, [selectedList]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  useEffect(() => {
    if (selectedList) {
      fetchMembers();
      fetchAnalytics();
    }
  }, [selectedList, fetchMembers, fetchAnalytics]);

  // Create new list
  const handleCreateList = async () => {
    try {
      const res = await fetch(`${API}/inner-circle/lists`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(newList)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Inner circle list created!");
        setShowCreateList(false);
        setNewList({
          name: "",
          owner_type: "org",
          owner_id: "",
          description: "",
          visibility: { cohosts_can_see: true, show_source_sublists: true }
        });
        fetchLists();
        setSelectedList(data.list);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create list");
      }
    } catch (err) {
      toast.error("Error creating list");
    }
  };

  // Add member
  const handleAddMember = async () => {
    if (!selectedList) return;
    try {
      const res = await fetch(`${API}/inner-circle/lists/${selectedList.list_id}/members`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          list_id: selectedList.list_id,
          ...newMember
        })
      });
      if (res.ok) {
        toast.success("Member added to inner circle!");
        setShowAddMember(false);
        setNewMember({
          name: "",
          email: "",
          company: "",
          title: "",
          source: "direct",
          source_detail: "",
          interests: [],
          notes: ""
        });
        fetchMembers();
        fetchAnalytics();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add member");
      }
    } catch (err) {
      toast.error("Error adding member");
    }
  };

  // Delete member
  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Remove this member from the inner circle?")) return;
    try {
      const res = await fetch(`${API}/inner-circle/members/${memberId}`, {
        method: "DELETE",
        headers: getHeaders()
      });
      if (res.ok) {
        toast.success("Member removed");
        fetchMembers();
        fetchAnalytics();
      }
    } catch (err) {
      toast.error("Error removing member");
    }
  };

  // Sync member to Beehiiv
  const syncToBeehiiv = async (member) => {
    try {
      const res = await fetch(`${API}/beehiiv/add-subscriber`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          email: member.email,
          send_welcome: false
        })
      });
      if (res.ok) {
        // Add inner circle tag
        await fetch(`${API}/beehiiv/add-tag`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({
            email: member.email,
            tags: [`inner_circle_${selectedList?.owner_id || "general"}`, `source_${member.source}`]
          })
        });
        toast.success(`Synced ${member.email} to Beehiiv with tags`);
      } else {
        toast.error("Failed to sync to Beehiiv");
      }
    } catch (err) {
      toast.error("Error syncing to Beehiiv");
    }
  };

  // Bulk sync all members
  const bulkSyncToBeehiiv = async () => {
    if (!window.confirm(`Sync all ${members.length} members to Beehiiv?`)) return;
    let synced = 0;
    for (const member of members) {
      try {
        await syncToBeehiiv(member);
        synced++;
      } catch (err) {
        // Continue with next
      }
    }
    toast.success(`Synced ${synced}/${members.length} members to Beehiiv`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <Users className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Admin Access Required</h3>
        <p className="text-gray-500 max-w-md mb-4">
          You need admin permissions to access Inner Circle management. 
          Please log in with an admin account or contact the platform owner.
        </p>
        <Button onClick={fetchLists} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inner-circle-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Inner Circle</h2>
          <p className="text-gray-500 mt-1">Manage your community lists with source tracking</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={bulkSyncToBeehiiv} disabled={!members.length}>
            <Mail className="w-4 h-4 mr-2" />
            Sync to Beehiiv
          </Button>
          <Button onClick={() => setShowCreateList(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New List
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Lists Sidebar */}
        <div className="col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500">Lists</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                {lists.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No lists yet. Create your first inner circle!
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {lists.map((list) => (
                      <button
                        key={list.list_id}
                        onClick={() => setSelectedList(list)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedList?.list_id === list.list_id
                            ? "bg-gray-900 text-white"
                            : "hover:bg-gray-100"
                        }`}
                        data-testid={`list-${list.list_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium truncate">{list.name}</span>
                          <Badge variant={selectedList?.list_id === list.list_id ? "secondary" : "outline"} className="ml-2">
                            {list.member_count || 0}
                          </Badge>
                        </div>
                        <div className="text-xs mt-1 opacity-70">
                          {list.owner_type === "series" ? "Event Series" : "Organization"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Source Analytics */}
          {analytics && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Source Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analytics.sources?.map((source) => (
                  <div key={source.source} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getSourceColor(source.source)}`} />
                      <span className="text-sm">{getSourceLabel(source.source)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{source.count}</span>
                      {source.converted > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {source.conversion_rate}% conv
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total Members</span>
                    <span className="font-bold">{analytics.total_members}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-500">Converted to RSVP</span>
                    <span className="font-medium text-green-600">{analytics.total_converted}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Content */}
        <div className="col-span-9">
          {selectedList ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selectedList.name}</CardTitle>
                    <CardDescription>{selectedList.description || "No description"}</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddMember(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search by name, email, or company..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="member-search"
                      />
                    </div>
                  </div>
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="w-48" data-testid="source-filter">
                      <SelectValue placeholder="Filter by source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      {SOURCE_OPTIONS.map((source) => (
                        <SelectItem key={source.value} value={source.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${source.color}`} />
                            {source.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchMembers}>
                    <RefreshCw className={`w-4 h-4 ${membersLoading ? "animate-spin" : ""}`} />
                  </Button>
                </div>

                {/* Members Table */}
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {membersLoading ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8">
                            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                          </TableCell>
                        </TableRow>
                      ) : members.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            No members yet. Add your first inner circle member!
                          </TableCell>
                        </TableRow>
                      ) : (
                        members.map((member) => (
                          <TableRow key={member.member_id} data-testid={`member-${member.member_id}`}>
                            <TableCell className="font-medium">{member.name}</TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>{member.company || "-"}</TableCell>
                            <TableCell>
                              <Badge className={`${getSourceColor(member.source)} text-white`}>
                                {getSourceLabel(member.source)}
                              </Badge>
                              {member.source_detail && (
                                <span className="text-xs text-gray-500 ml-1">({member.source_detail})</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {member.converted_to_rsvp ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Converted
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {new Date(member.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowMemberDetail(member)}
                                  title="View details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => syncToBeehiiv(member)}
                                  title="Sync to Beehiiv"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteMember(member.member_id)}
                                  className="text-red-500 hover:text-red-700"
                                  title="Remove"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold">{members.length}</div>
                    <div className="text-sm text-gray-500">Total Members</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {members.filter(m => m.converted_to_rsvp).length}
                    </div>
                    <div className="text-sm text-gray-500">Converted</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">
                      {members.filter(m => new Date(m.created_at) > new Date(Date.now() - 7*24*60*60*1000)).length}
                    </div>
                    <div className="text-sm text-gray-500">This Week</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">
                      {new Set(members.map(m => m.source)).size}
                    </div>
                    <div className="text-sm text-gray-500">Sources</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-16 text-center">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No List Selected</h3>
                <p className="text-gray-500 mt-1">Select a list from the sidebar or create a new one</p>
                <Button className="mt-4" onClick={() => setShowCreateList(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Inner Circle
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create List Modal */}
      <Dialog open={showCreateList} onOpenChange={setShowCreateList}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Inner Circle List</DialogTitle>
            <DialogDescription>
              Create a new inner circle for an organization or event series
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="list-name">List Name</Label>
              <Input
                id="list-name"
                placeholder="e.g., HTHF Inner Circle"
                value={newList.name}
                onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                data-testid="new-list-name"
              />
            </div>
            <div>
              <Label>Owner Type</Label>
              <Select
                value={newList.owner_type}
                onValueChange={(v) => setNewList({ ...newList, owner_type: v })}
              >
                <SelectTrigger data-testid="new-list-owner-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">Organization</SelectItem>
                  <SelectItem value="series">Event Series</SelectItem>
                  <SelectItem value="pilot">Pilot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="owner-id">Owner ID</Label>
              <Input
                id="owner-id"
                placeholder="e.g., partner_org or event_series"
                value={newList.owner_id}
                onChange={(e) => setNewList({ ...newList, owner_id: e.target.value })}
                data-testid="new-list-owner-id"
              />
            </div>
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Describe this inner circle..."
                value={newList.description}
                onChange={(e) => setNewList({ ...newList, description: e.target.value })}
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Co-hosts can see members</Label>
                <Switch
                  checked={newList.visibility.cohosts_can_see}
                  onCheckedChange={(v) => setNewList({
                    ...newList,
                    visibility: { ...newList.visibility, cohosts_can_see: v }
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Show source sub-lists</Label>
                <Switch
                  checked={newList.visibility.show_source_sublists}
                  onCheckedChange={(v) => setNewList({
                    ...newList,
                    visibility: { ...newList.visibility, show_source_sublists: v }
                  })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateList(false)}>Cancel</Button>
            <Button onClick={handleCreateList} disabled={!newList.name || !newList.owner_id}>
              Create List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Modal */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add to Inner Circle</DialogTitle>
            <DialogDescription>
              Add a new member to {selectedList?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="member-name">Name *</Label>
                <Input
                  id="member-name"
                  placeholder="Full name"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  data-testid="new-member-name"
                />
              </div>
              <div>
                <Label htmlFor="member-email">Email *</Label>
                <Input
                  id="member-email"
                  type="email"
                  placeholder="email@example.com"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  data-testid="new-member-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="member-company">Company</Label>
                <Input
                  id="member-company"
                  placeholder="Company name"
                  value={newMember.company}
                  onChange={(e) => setNewMember({ ...newMember, company: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="member-title">Title</Label>
                <Input
                  id="member-title"
                  placeholder="Job title"
                  value={newMember.title}
                  onChange={(e) => setNewMember({ ...newMember, title: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source *</Label>
                <Select
                  value={newMember.source}
                  onValueChange={(v) => setNewMember({ ...newMember, source: v })}
                >
                  <SelectTrigger data-testid="new-member-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map((source) => (
                      <SelectItem key={source.value} value={source.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${source.color}`} />
                          {source.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="source-detail">Source Detail</Label>
                <Input
                  id="source-detail"
                  placeholder="e.g., March 2026 Event"
                  value={newMember.source_detail}
                  onChange={(e) => setNewMember({ ...newMember, source_detail: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="member-notes">Notes</Label>
              <Textarea
                id="member-notes"
                placeholder="Any additional notes..."
                value={newMember.notes}
                onChange={(e) => setNewMember({ ...newMember, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
            <Button onClick={handleAddMember} disabled={!newMember.name || !newMember.email}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Detail Modal */}
      <Dialog open={!!showMemberDetail} onOpenChange={() => setShowMemberDetail(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{showMemberDetail?.name}</DialogTitle>
            <DialogDescription>{showMemberDetail?.email}</DialogDescription>
          </DialogHeader>
          {showMemberDetail && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Company</Label>
                  <p className="font-medium">{showMemberDetail.company || "-"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Title</Label>
                  <p className="font-medium">{showMemberDetail.title || "-"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Source</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`${getSourceColor(showMemberDetail.source)} text-white`}>
                      {getSourceLabel(showMemberDetail.source)}
                    </Badge>
                    {showMemberDetail.source_detail && (
                      <span className="text-sm text-gray-500">({showMemberDetail.source_detail})</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Status</Label>
                  <div className="mt-1">
                    {showMemberDetail.converted_to_rsvp ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Converted to RSVP
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending Conversion
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {showMemberDetail.interests?.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs">Interests</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {showMemberDetail.interests.map((interest, i) => (
                      <Badge key={i} variant="secondary">{interest}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {showMemberDetail.notes && (
                <div>
                  <Label className="text-gray-500 text-xs">Notes</Label>
                  <p className="text-sm mt-1">{showMemberDetail.notes}</p>
                </div>
              )}
              <div className="pt-4 border-t">
                <Label className="text-gray-500 text-xs">Beehiiv Tags</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Badge variant="outline" className="font-mono text-xs">
                    inner_circle_{selectedList?.owner_id}
                  </Badge>
                  <Badge variant="outline" className="font-mono text-xs">
                    source_{showMemberDetail.source}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMemberDetail(null)}>Close</Button>
            <Button onClick={() => {
              syncToBeehiiv(showMemberDetail);
              setShowMemberDetail(null);
            }}>
              <Mail className="w-4 h-4 mr-2" />
              Sync to Beehiiv
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
